import { Link } from 'react-router-dom';
import { Check, CheckCheck, ChevronsUp, FileCheck, LocateFixed, MapPin, Waypoints } from 'lucide-react';
import type { Alerta, MotivoParadaNoPlanificada } from '@tms/contratos';
import { escalo } from '../util/alertas';
import { fmtCoordenadas, fmtDuracionMin, fmtFechaHora, fmtHace, fmtNum } from '../util/format';
import { ChipEstado } from './ChipEstado';
import { ICONO_TIPO_ALERTA, MOTIVO_NO_PLANIFICADA, NIVEL } from './estados';

function num(d: Record<string, unknown>, k: string): number | null {
  const v = d[k];
  return typeof v === 'number' ? v : null;
}
function txt(d: Record<string, unknown>, k: string): string | null {
  const v = d[k];
  return typeof v === 'string' ? v : null;
}

/** Descripción legible a partir del `detalle` propio de cada tipo (ver contrato Alerta). */
export function describirAlerta(a: Alerta): string {
  const d = a.detalle;
  const min = num(d, 'minutos');
  switch (a.tipo) {
    case 'EXCESO_VELOCIDAD':
      return `Registró ${fmtNum(num(d, 'velocidadKmh'))} km/h. Límite: ${fmtNum(num(d, 'maximaKmh'))} km/h.`;
    case 'DESVIO_RUTA': {
      const m = num(d, 'distanciaM');
      return `La unidad se alejó ${m === null ? '—' : fmtNum(m / 1000, 1)} km de la traza de la ruta asignada.`;
    }
    case 'CAMBIO_RUTA':
      return 'La unidad va por otra ruta activa de la troncal: se reasignó la ruta del viaje.';
    case 'PARADA_NO_AUTORIZADA':
      return `Detenida${min === null ? '' : ` ${fmtDuracionMin(min)}`} fuera de las paradas de la ruta y sin parada declarada por el chofer.`;
    case 'PARADA_NO_PLANIFICADA': {
      const motivo = txt(d, 'motivo');
      const etiqueta = motivo && motivo in MOTIVO_NO_PLANIFICADA ? MOTIVO_NO_PLANIFICADA[motivo as MotivoParadaNoPlanificada] : motivo;
      const obs = txt(d, 'observacion');
      return `El chofer declaró una parada fuera de la ruta${etiqueta ? ` (${etiqueta.toLocaleLowerCase('es')})` : ''}${obs ? `: «${obs}»` : ''}.`;
    }
    case 'PARADA_EXCEDIDA': {
      const perm = num(d, 'permanenciaMin');
      const max = num(d, 'duracionMaxEsperadaMin');
      return `Permaneció ${fmtDuracionMin(perm)} en la parada; lo esperado era hasta ${fmtDuracionMin(max)}.`;
    }
    case 'PARADA_OMITIDA': {
      const orden = num(d, 'orden');
      return `El viaje terminó sin pasar por la parada obligatoria${orden === null ? '' : ` n.º ${orden}`}.`;
    }
    case 'RALENTI_EXCESIVO':
      return `Motor en ralentí${min === null ? '' : ` durante ${fmtDuracionMin(min)}`}.`;
    case 'SIN_REPORTE':
      return `Sin comunicación${min === null ? '' : ` durante ${fmtDuracionMin(min)}`}.`;
    case 'INICIO_DEMORADO':
      return `El viaje no inició${min === null ? '' : ` y lleva ${fmtDuracionMin(min)} de demora`} respecto de la hora planificada.`;
    case 'RETRASO_ETA': {
      const r = num(d, 'minutosRetraso');
      return `Arribo estimado${r === null ? '' : ` ${fmtDuracionMin(r)}`} después del horario previsto.`;
    }
    case 'RECURSO_INHABILITADO':
      return `${txt(d, 'recurso') === 'VEHICULO' ? 'Vehículo' : 'Chofer'} inhabilitado en Mara${txt(d, 'detalle') ? `: ${txt(d, 'detalle')}` : ''}.`;
    case 'INCONSISTENCIA_MARCACION': {
      const m = num(d, 'distanciaM');
      return `El aviso de Mara no coincide con el GPS${m === null ? '' : ` (a ${fmtNum(m)} m del último reporte de Microtrack)`}.`;
    }
    case 'VIAJE_SIN_RUTA':
      return 'La troncal del viaje no tiene rutas en el TMS: no se evalúan desvíos, ETA ni paradas hasta rutearla.';
  }
}

/** Nombre del lugar del contrato; si no hay, las coordenadas. */
function lugarDe(a: Alerta): string | null {
  return a.ubicacionNombre ?? (a.ubicacion ? fmtCoordenadas(a.ubicacion.latitud, a.ubicacion.longitud) : null);
}

/** Troncal a rutear, si la alerta es VIAJE_SIN_RUTA y trae su id. */
export function troncalARutear(a: Alerta): number | null {
  return a.tipo === 'VIAJE_SIN_RUTA' ? num(a.detalle, 'troncalId') : null;
}

/** Marca de alerta escalada: ícono + texto, con el nivel con el que nació en el título. */
export function MarcaEscalada({ alerta }: { alerta: Alerta }) {
  if (!escalo(alerta) || alerta.nivelInicial === null) return null;
  const base = NIVEL[alerta.nivelInicial].etiqueta.toLocaleLowerCase('es');
  return (
    <span className="escalo" title={`Nació como ${base} y subió de nivel mientras seguía abierta.`}>
      <ChevronsUp aria-hidden />
      Escaló
    </span>
  );
}

interface Props {
  alerta: Alerta;
  ahora: number;
  onVerEnMapa?: (a: Alerta) => void;
  onResolver: (a: Alerta) => void;
  onJustificar: (a: Alerta) => void;
}

/** Fila de la bandeja de novedades (estructura del diseño: .alert). */
export function FilaAlerta({ alerta: a, ahora, onVerEnMapa, onResolver, onJustificar }: Props) {
  const Icono = ICONO_TIPO_ALERTA[a.tipo];
  const abierta = a.estado === 'ABIERTA';
  const lugar = lugarDe(a);
  const troncalId = troncalARutear(a);
  return (
    <article className={`alert nivel-${a.nivel} ${abierta ? '' : 'closed'}`}>
      <div className={`alert-top ${NIVEL[a.nivel].color}`}>
        <Icono aria-hidden />
        <span>{a.titulo}</span>
        <time dateTime={a.inicio} title={fmtFechaHora(a.inicio)}>
          {fmtHace(a.inicio, ahora)}
        </time>
      </div>
      <div className="alert-nivel">
        <ChipEstado tipo="nivel" valor={a.nivel} />
        <MarcaEscalada alerta={a} />
      </div>
      <p className="alert-unit">
        <strong>{a.patente ?? a.vehiculoIdMicrotrack ?? 'Sin unidad'}</strong> <span>{a.choferNombre ? `· ${a.choferNombre}` : ''}</span>
        {a.viajeIdMara && <span> · {a.viajeIdMara}</span>}
      </p>
      {lugar && (
        <p className="alert-place">
          <MapPin aria-hidden />
          {lugar}
        </p>
      )}
      <p className="alert-desc">{describirAlerta(a)}</p>
      {abierta ? (
        <div className="alert-actions">
          {a.ubicacion && onVerEnMapa && (
            <button type="button" onClick={() => onVerEnMapa(a)}>
              <LocateFixed aria-hidden />
              Ver en mapa
            </button>
          )}
          {troncalId !== null && (
            <Link className="boton-link" to={`/troncales/${troncalId}`}>
              <Waypoints aria-hidden />
              Rutear troncal
            </Link>
          )}
          <button type="button" className="res" onClick={() => onResolver(a)}>
            <Check aria-hidden />
            Resolver
          </button>
          <button type="button" onClick={() => onJustificar(a)}>
            <FileCheck aria-hidden />
            Justificar
          </button>
        </div>
      ) : (
        <>
          <p className={`badge ${a.estado === 'RESUELTA' ? 'green' : 'muted'}`}>
            <CheckCheck aria-hidden />
            {a.estado === 'RESUELTA' ? 'Resuelta' : 'Justificada'}
            {a.cerradaAutomaticamente ? ' automáticamente' : a.cambiadoPor ? ` por ${a.cambiadoPor.nombre}` : ''}
            {a.cambiadoEn ? ` · ${fmtFechaHora(a.cambiadoEn)}` : ''}
          </p>
          {a.motivo && <p className="motivo">{a.motivo}</p>}
        </>
      )}
    </article>
  );
}
